import React, { useState, useEffect, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';

import Box from '../../../../components/Box/Box';
import CustomText from '../../../../components/Typography/CustomText/CustomText';
import EmptyState from '../../../../components/EmptyState/EmptyState';

import SliderErrorsDetails, { ErrorStateType } from './SliderErrorsDetails';
import SliderErrorsFilter from './SliderErrorsFilter';
import { ErrorType, OnSliderErrorsSubmitType, SpreadsheetCallbackType, SpreadsheetColumnType } from '../../types';

/**
  This function will translate coordinates like 0,0 into A1
  3,5 into C6, 30,30 into AD31 and so on. We add 1 to the row, as row
  0 means nothing to normal, not developer humans. This function
  only works to a max of 701 columns. Which we all hope is enough.
*/
const getCoords = ({ col, row }) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  return `${alphabet[parseInt(`${col / 26}`, 10) - 1] || ''}${alphabet[col % 26]}${row + 1}`;
};

const defaultItemHeight = 54;

export interface SliderErrorProps {
  getDataAtCell: SpreadsheetCallbackType['getDataAtCell'];
  setDataAtCell: SpreadsheetCallbackType['setDataAtCell'];
  columns: SpreadsheetColumnType[];
  onSliderErrorsSubmit?: OnSliderErrorsSubmitType;
  errors: ErrorType[] | undefined;
}

export type EnhancedErrorType = ErrorType & {
  colHeader: string;
  colType: string;
  colSourceOptions?: SpreadsheetColumnType['source'];
  humanCoords: string;
  externalErrorMessage?: string;
  hasExternalError?: boolean;
};

const SliderErrors = (props: SliderErrorProps) => {
  const { errors, getDataAtCell, setDataAtCell, columns, onSliderErrorsSubmit } = props;
  const [selectedErrors, setSelectedErrors] = useState<EnhancedErrorType[]>(
    [],
  ); /* this works as the filter's state too */

  const [errorsState, setErrorsState] = useState<Record<number, ErrorStateType>>({});

  /*
    We need the value of allErrors to persist through filtering and rerendering.
    Since this is being passed into the filter, and being set inside a useEffect,
    we need to save it on a ref so we don't lose it throughout this whole process.
  */
  const allErrors = useRef<EnhancedErrorType[]>([]);

  useEffect(() => {
    if (!errors) {
      return;
    }

    allErrors.current = errors.map(error => {
      const { col } = error;

      const humanCoords = getCoords(error);
      const colHeader = columns[col].title || '';
      const colType = columns[col].type || '';
      const colSourceOptions = columns[col]?.source;

      return { ...error, colHeader, humanCoords, colType, colSourceOptions };
    });

    if (allErrors.current.length) {
      setSelectedErrors(allErrors.current);
    } else {
      setSelectedErrors([]);
    }
  }, [errors, columns, setSelectedErrors]);

  const handleToggleCallback = (errorId, value) => {
    setErrorsState({
      ...errorsState,
      [errorId]: {
        open: value ? errorsState[errorId]?.open : !errorsState[errorId]?.open,
        value: value || errorsState[errorId]?.value,
      },
    });
  };

  return (
    <>
      <Box display="flex" flexDirection="column" minHeight="100%" data-testid="slider-errors">
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <CustomText size="20" color="inherit" weight="semibold">
            Errors
          </CustomText>
          {selectedErrors.length !== 0 && (
            <SliderErrorsFilter errors={allErrors.current} setSelectedErrors={setSelectedErrors} />
          )}
        </Box>

        {selectedErrors.length > 0 && (
          <Virtuoso
            style={{ flex: '1' }}
            increaseViewportBy={defaultItemHeight * 15}
            totalCount={selectedErrors.length}
            defaultItemHeight={defaultItemHeight}
            itemContent={index => {
              const error = selectedErrors[index];
              return (
                <SliderErrorsDetails
                  key={`error-${error.col}-${error.row}`}
                  index={index}
                  error={error}
                  getDataAtCell={getDataAtCell}
                  setDataAtCell={setDataAtCell}
                  onSliderErrorsSubmit={onSliderErrorsSubmit}
                  onToggleCallback={handleToggleCallback}
                  errorState={errorsState[`${error.col}-${error.row}`] || {}}
                />
              );
            }}
          />
        )}
        {selectedErrors.length === 0 && <EmptyState type="block" text="You're good, no errors!" icon="trophy" />}
      </Box>
    </>
  );
};

export default SliderErrors;
